import React, { useState } from "react";
import { Checkbox, CheckboxGroup } from "@design-system/Checkbox/Checkbox";
import { CheckboxIndicator } from "@design-system/Checkbox/CheckboxIndicator";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { useAnatomyMode } from "./componentAnatomyMode";
import { BooleanControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import checkboxStyles from "./CheckboxPlayground.module.scss";
import styles from "./DatePickerPlayground.module.scss";

interface MatrixStateProps {
    disabled?: boolean;
    "data-forced-state"?: "hover" | "pressed" | "focus";
}

const matrixStates: ReadonlyArray<{
    id: string;
    label: string;
    props: MatrixStateProps;
}> = [
    { id: "normal", label: "Normal", props: {} },
    { id: "hover", label: "Hover", props: { "data-forced-state": "hover" } },
    {
        id: "pressed",
        label: "Pressed",
        props: { "data-forced-state": "pressed" },
    },
    { id: "focus", label: "Focus", props: { "data-forced-state": "focus" } },
    { id: "disabled", label: "Disabled", props: { disabled: true } },
];

const matrixVariants = ["unchecked", "checked", "indeterminate"] as const;

type MatrixVariant = (typeof matrixVariants)[number];

const MatrixCheckbox = ({
    variant,
    stateProps,
}: {
    variant: MatrixVariant;
    stateProps: MatrixStateProps;
}) => (
    <Checkbox
        checked={variant === "checked"}
        indeterminate={variant === "indeterminate"}
        label={variant}
        disabled={stateProps.disabled}
        data-forced-state={stateProps["data-forced-state"]}
    />
);

const CheckboxMatrix = () => (
    <VStack className={checkboxStyles.section} gap={24} padding={24}>
        <h2 className={checkboxStyles.sectionTitle}>State matrix</h2>
        <div className={checkboxStyles.grid}>
            <span className={checkboxStyles.columnHeader}>State</span>
            {matrixVariants.map((variant) => (
                <span key={variant} className={checkboxStyles.columnHeader}>
                    {variant}
                </span>
            ))}
            {matrixStates.map((state) => (
                <React.Fragment key={state.id}>
                    <span className={checkboxStyles.rowLabel}>
                        {state.label}
                    </span>
                    {matrixVariants.map((variant) => (
                        <div
                            key={variant}
                            className={checkboxStyles.matrixCell}
                        >
                            <MatrixCheckbox
                                variant={variant}
                                stateProps={state.props}
                            />
                        </div>
                    ))}
                </React.Fragment>
            ))}
        </div>
    </VStack>
);

const groupValues = ["invoices", "receipts", "statements"];

const sandboxLabel = "Include pending transactions";

const CheckboxGroupDemo = () => {
    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    return (
        <VStack className={checkboxStyles.section} gap={24} padding={24}>
            <h2 className={checkboxStyles.sectionTitle}>Group demo</h2>
            <HStack gap={40} align="start" wrap>
                <VStack gap={8}>
                    <h3 className={checkboxStyles.subsectionTitle}>
                        Parent selection
                    </h3>
                    <CheckboxGroup
                        aria-label="Financial documents"
                        value={selectedValues}
                        onValueChange={setSelectedValues}
                        allValues={groupValues}
                    >
                        <Checkbox parent label="All" />
                        <Checkbox name="invoices" label="Invoices" />
                        <Checkbox name="receipts" label="Receipts" />
                        <Checkbox name="statements" label="Bank statements" />
                    </CheckboxGroup>
                </VStack>
                <VStack gap={12}>
                    <h3 className={checkboxStyles.subsectionTitle}>
                        Label examples
                    </h3>
                    <HStack gap={40} align="start" wrap>
                        <VStack gap={8}>
                            <span className={checkboxStyles.exampleLabel}>
                                Bare control
                            </span>
                            <Checkbox aria-label="Select row" />
                        </VStack>
                        <VStack gap={8}>
                            <span className={checkboxStyles.exampleLabel}>
                                Rich label
                            </span>
                            <Checkbox
                                label={
                                    <VStack as="span" gap={2}>
                                        <span
                                            className={checkboxStyles.richTitle}
                                        >
                                            Include pending transactions
                                        </span>
                                        <span
                                            className={
                                                checkboxStyles.richDescription
                                            }
                                        >
                                            Show transactions that have not
                                            posted yet.
                                        </span>
                                    </VStack>
                                }
                            />
                        </VStack>
                        <VStack gap={8}>
                            <span className={checkboxStyles.exampleLabel}>
                                Label placement
                            </span>
                            <VStack
                                className={checkboxStyles.labelPlacementExample}
                                gap={8}
                            >
                                <Checkbox
                                    label="Income"
                                    labelPlacement="start"
                                />
                                <Checkbox
                                    label="Expenses"
                                    labelPlacement="start"
                                />
                            </VStack>
                        </VStack>
                    </HStack>
                </VStack>
            </HStack>
        </VStack>
    );
};

const indicatorStates = ["unchecked", "checked", "indeterminate"] as const;

const CheckboxIndicators = () => (
    <VStack className={checkboxStyles.section} gap={16} padding={24}>
        <VStack gap={4}>
            <h2 className={checkboxStyles.sectionTitle}>Static indicators</h2>
            <span className={checkboxStyles.sectionDescription}>
                Non-interactive checkbox visuals for dropdown items.
            </span>
        </VStack>
        <HStack gap={32} align="start" wrap>
            {indicatorStates.map((state) => (
                <React.Fragment key={state}>
                    <VStack gap={8}>
                        <CheckboxIndicator state={state} />
                        <span className={checkboxStyles.exampleLabel}>
                            {state}
                        </span>
                    </VStack>
                    <VStack gap={8}>
                        <CheckboxIndicator state={state} disabled />
                        <span className={checkboxStyles.exampleLabel}>
                            {state} disabled
                        </span>
                    </VStack>
                </React.Fragment>
            ))}
        </HStack>
    </VStack>
);

export const CheckboxPlayground = () => {
    const [isChecked, setIsChecked] = useState(false);
    const [isIndeterminate, setIsIndeterminate] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [hasLabel, setHasLabel] = useState(true);
    let state = "unchecked";

    if (isIndeterminate) {
        state = "indeterminate";
    } else if (isChecked) {
        state = "checked";
    }

    // `state` is already the spec's value axis, so the Checked / Indeterminate
    // controls drive the x-ray without a picker of their own.
    const anatomy = useAnatomyMode({
        component: "checkbox",
        variantId: state,
        isDisabled,
    });

    const liveSummary = `${state} · ${isDisabled ? "disabled" : "enabled"}`;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Checked"
                                checked={isChecked}
                                onChange={setIsChecked}
                            />
                            <BooleanControl
                                label="Indeterminate"
                                checked={isIndeterminate}
                                onChange={setIsIndeterminate}
                            />
                            <BooleanControl
                                label="Disabled"
                                checked={isDisabled}
                                onChange={setIsDisabled}
                            />
                            <BooleanControl
                                label="Label"
                                checked={hasLabel}
                                onChange={setHasLabel}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={
                    anatomy.isActive ? `x-ray · ${liveSummary}` : liveSummary
                }
                belowStage={
                    <>
                        <CheckboxMatrix />
                        <CheckboxGroupDemo />
                        <CheckboxIndicators />
                    </>
                }
                output={JSON.stringify(
                    {
                        checked: isChecked,
                        indeterminate: isIndeterminate,
                        disabled: isDisabled,
                        label: hasLabel,
                        forcedState: anatomy.forcedState ?? null,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <Checkbox
                        checked={isChecked}
                        onCheckedChange={setIsChecked}
                        indeterminate={isIndeterminate}
                        disabled={isDisabled}
                        label={hasLabel ? sandboxLabel : undefined}
                        aria-label={hasLabel ? undefined : sandboxLabel}
                        data-forced-state={anatomy.forcedState}
                    />
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
